import React from 'react'
import { FormProvider } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { Card } from 'primereact/card'
import { Button } from 'primereact/button'

import { isAdminDomain, isPartnerDomain, isSupportDomain } from '../../../lib/currentDomain'
import { RootState } from '../../../partner/redux/store'
import { mapFormValuesToChargerInformation } from '../../utils/objectMappers'
import { IChargerInformationFormInput, useChargerInfoValidation } from '../../hooks/useChargePointInfoValidation'
import {
    ChargePoint,
    useChargePointUpdateChargerInformationMutation,
    usePartnerGetExternalServersQuery,
} from '../../redux/api/web-api'
import useText from '../../hooks/useText'
import { useToast } from '../../uikit/ToastProvider'

import ChargePointDetailsAdminInputs from './chargepoint-details-admin-inputs'
import ChargePointDetailsPartnerInputs from './chargepoint-details-partner-inputs'
import ChargePointDetailsSupportInputs from './chargepoint-details-support-inputs'

export type ChargepointDetailsWidgetProps = {
    chargePoint: ChargePoint
}

export function ChargepointDetailsWidget({ chargePoint }: ChargepointDetailsWidgetProps): React.ReactElement {
    const text = useText()
    const partnerId = useSelector((state: RootState) => state.user.partnerId)
    const { data: externalServersForPartner } = usePartnerGetExternalServersQuery(
        { partnerId: partnerId ?? 0 },
        { skip: !isPartnerDomain }
    )
    const chargerInfoFormValidation = useChargerInfoValidation(chargePoint)
    const {
        handleSubmit,
        reset,
        formState: { isDirty },
    } = chargerInfoFormValidation
    const toast = useToast()
    const [updateChargerInfo, { isLoading: chargerInfoUpdating }] = useChargePointUpdateChargerInformationMutation()

    const saveChargePoint = (formValues: IChargerInformationFormInput) => {
        updateChargerInfo({
            chargePointId: chargePoint.id,
            chargerInformation: mapFormValuesToChargerInformation(formValues),
        })
            .unwrap()
            .then(() => {
                toast.notifySuccess(text('msgChargerUpdated'))

                reset(formValues, {
                    keepDirty: false,
                    keepDirtyValues: false,
                })
            })
            .catch(() => toast.notifyError(text('msgChargePointNotUpdated')))
    }

    return (
        <div data-testid={`chargePoint-${chargePoint?.id}`}>
            <Card>
                <FormProvider {...chargerInfoFormValidation}>
                    {isAdminDomain ? <ChargePointDetailsAdminInputs chargePoint={chargePoint} /> : null}
                    {isPartnerDomain ? (
                        <ChargePointDetailsPartnerInputs
                            chargePoint={chargePoint}
                            externalServersForPartner={externalServersForPartner}
                        />
                    ) : null}

                    {isSupportDomain && <ChargePointDetailsSupportInputs chargePoint={chargePoint} />}

                    {(isAdminDomain || (isPartnerDomain && !chargePoint.ocppEnabled)) && (
                        <div className="flex justify-content-end">
                            <Button
                                label={text('btnSave')}
                                onClick={handleSubmit(saveChargePoint)}
                                disabled={chargerInfoUpdating || !isDirty}
                            />
                        </div>
                    )}
                </FormProvider>
            </Card>
        </div>
    )
}
